import {
  axiosFieldValidationErrorToFormErrors,
  hasFieldValidationError,
  type VerifiedEmailsResponse,
} from '@gofranz/common';
import { parseApiError } from '@gofranz/common-components';
import type { Form, NewFormsRecipient, UpdateForm } from '@gofranz/formshive-common';
import {
  Accordion,
  Alert,
  Button,
  Group,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowRight,
  IconChecklist,
  IconInfoCircle,
  IconLock,
  IconMail,
  IconMailForward,
  IconSettings,
  IconWebhook,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import { clearField } from '../../lib/clear-field';
import { type FormSpec, parseAndValidateFormSpec } from '../../lib/form-specs';
import { validateTemplateString } from '../../lib/template-validation';
import { useRustyState } from '../../state';
import { FormsIntegrationsPanel } from '../FormIntegrations/Panel';
import { FormsRecipientDetail } from '../FormsRecipient/Detail';
import { AutoResponse } from './AutoResponse';
import { FormFields, type FormFieldsValues } from './Common';
import { FormEditActionBar } from './FormEditActionBar';
import { LockedSectionModal, type LockedSectionPrerequisite } from './LockedSectionModal';

// Types for distributed state
export interface FormDataRef {
  // FormFields section
  title: string;
  filter_spam: boolean;
  check_challenge: boolean;
  redirect_url: string | undefined;
  // Form Fields (spec) section — edited via the dedicated Field Editor route
  check_specs: boolean;
  specs: string | undefined;
  // AutoResponse section
  auto_response_enabled: boolean;
  auto_response_subject: string | undefined;
  auto_response_text: string | undefined;
  // Email format
  email_notification_format: string;
  auto_response_format: string;
  // Data retention
  retention_days: number | null;
  allowed_origins: string[];
}

export interface EditFormProps {
  submitFormCb: (id: string, updatedForm: UpdateForm) => Promise<void>;
  form: Form;
  formSubmitUrl: string;
  formChallengeUrl: string;
  formChallengeUrlNoTrack?: string;

  // Hoisted from parent - parsed once
  parsedSpec: FormSpec | null;
  hasFormSpec: boolean;
  hasFileField: boolean;

  submitFormRecipientCb: (newForm: NewFormsRecipient) => Promise<void>;
  getVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  getFormVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  deleteRecipientCp: (formId: string, recipientId: string) => Promise<void>;

  onFormDataRef?: (ref: React.RefObject<FormDataRef>) => void;
  chatOpened: boolean;
  onToggleChat: () => void;
  onDelete: () => void;
}

type RowTone = 'gray' | 'yellow';

// Amber left border = "this section needs your input".
function attentionItemStyle(tone: RowTone): React.CSSProperties | undefined {
  if (tone !== 'yellow') return undefined;
  return { borderLeft: '3px solid var(--mantine-color-yellow-5)' };
}

interface AccordionRowHeaderProps {
  icon: React.ReactNode;
  title: string;
  summary: string;
  tone: RowTone;
  locked?: boolean;
}

function AccordionRowHeader(props: AccordionRowHeaderProps) {
  const summaryColor = props.locked ? 'dimmed' : props.tone === 'yellow' ? 'yellow.8' : 'dimmed';

  return (
    <Group wrap="nowrap" gap="md">
      <ThemeIcon
        variant="light"
        size={36}
        radius="xl"
        color={props.tone === 'yellow' ? 'yellow' : 'gray'}
      >
        {props.icon}
      </ThemeIcon>
      <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
        <Group gap={6} wrap="nowrap">
          <Text fw={500}>{props.title}</Text>
          {props.locked && <IconLock size={14} color="var(--mantine-color-dimmed)" />}
        </Group>
        <Text size="sm" c={summaryColor} fw={400}>
          {props.summary}
        </Text>
      </Stack>
    </Group>
  );
}

export function EditForm(props: EditFormProps) {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [recipientCount, setRecipientCount] = useState(0);
  const [integrationCount, setIntegrationCount] = useState(0);
  const [notificationFormat, setNotificationFormat] = useState(
    props.form.email_notification_format
  );
  const [autoResponseFormat, setAutoResponseFormat] = useState(props.form.auto_response_format);

  // Controlled accordion so the lock modal can programmatically expand a section.
  const [openAccordionItem, setOpenAccordionItem] = useState<string | null>(null);
  const [lockModalOpen, setLockModalOpen] = useState(false);

  const hasEmailRecipients = recipientCount > 0;

  // Derive max_retention_days from the user's current subscription plan
  const { subscriptionPlans, currentSubscription } = useRustyState();
  const maxRetentionDays = useMemo(() => {
    if (!subscriptionPlans || !currentSubscription) return 30; // default to free tier
    const plan = subscriptionPlans.find((p) => p.id === currentSubscription.plan_id);
    return plan?.max_retention_days ?? null; // null means unlimited
  }, [subscriptionPlans, currentSubscription]);

  // AutoResponse validation state
  const [validateSubject, setValidateSubject] = useState(false);
  const [validateText, setValidateText] = useState(false);

  // ARCHITECTURAL CHANGE: Use ref to collect form data from distributed child components
  // Each section owns its state; this ref aggregates for save operations
  const formDataRef = useRef<FormDataRef>({
    title: props.form.title,
    filter_spam: props.form.filter_spam,
    check_challenge: props.form.check_challenge,
    redirect_url: props.form.redirect_url || undefined,
    check_specs: props.form.check_specs,
    specs: props.form.specs || undefined,
    auto_response_enabled: props.form.auto_response_enabled,
    auto_response_subject: props.form.auto_response_subject || undefined,
    auto_response_text: props.form.auto_response_text || undefined,
    email_notification_format: props.form.email_notification_format,
    auto_response_format: props.form.auto_response_format,
    retention_days: props.form.retention_days ?? null,
    allowed_origins: props.form.allowed_origins ?? [],
  });

  // Original values for dirty comparison (immutable reference to initial props)
  const originalValuesRef = useRef<FormDataRef>({
    title: props.form.title,
    filter_spam: props.form.filter_spam,
    check_challenge: props.form.check_challenge,
    redirect_url: props.form.redirect_url || undefined,
    check_specs: props.form.check_specs,
    specs: props.form.specs || undefined,
    auto_response_enabled: props.form.auto_response_enabled,
    auto_response_subject: props.form.auto_response_subject || undefined,
    auto_response_text: props.form.auto_response_text || undefined,
    email_notification_format: props.form.email_notification_format,
    auto_response_format: props.form.auto_response_format,
    retention_days: props.form.retention_days ?? null,
    allowed_origins: props.form.allowed_origins ?? [],
  });

  // Expose formDataRef to parent for chat assistant
  useEffect(() => {
    props.onFormDataRef?.(formDataRef);
  }, [props.onFormDataRef]);

  // Trigger re-render when dirty state might have changed
  const [dirtyCheckCounter, setDirtyCheckCounter] = useState(0);

  // Count changed fields against the original snapshot; drives the bottom action bar status text.
  const dirtyCount = useMemo(() => {
    const current = formDataRef.current;
    const original = originalValuesRef.current;
    let count = 0;
    if (current.title !== original.title) count++;
    if (current.filter_spam !== original.filter_spam) count++;
    if (current.check_challenge !== original.check_challenge) count++;
    if (current.redirect_url !== original.redirect_url) count++;
    if (current.check_specs !== original.check_specs) count++;
    if (current.specs !== original.specs) count++;
    if (current.auto_response_enabled !== original.auto_response_enabled) count++;
    if (current.auto_response_subject !== original.auto_response_subject) count++;
    if (current.auto_response_text !== original.auto_response_text) count++;
    if (current.email_notification_format !== original.email_notification_format) count++;
    if (current.auto_response_format !== original.auto_response_format) count++;
    if (current.retention_days !== original.retention_days) count++;
    if (current.allowed_origins.join('\n') !== original.allowed_origins.join('\n')) count++;
    return count;
  }, [dirtyCheckCounter]);
  const isDirty = dirtyCount > 0;

  useUnsavedChangesWarning({
    when: isDirty,
    title: t('formEdit.unsavedChangesTitle'),
    body: t('formEdit.unsavedChangesBody'),
    confirmLabel: t('formEdit.unsavedChangesDiscard'),
    cancelLabel: t('formEdit.unsavedChangesStay'),
  });

  // For form field errors (set on submit failure)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Check if form has email recipients
  useEffect(() => {
    const checkEmailRecipients = async () => {
      try {
        const data = await props.getFormVerifiedEmails();
        setRecipientCount(data.data.length);
      } catch (e) {
        console.error('Error checking email recipients:', e);
        setRecipientCount(0);
      }
    };
    checkEmailRecipients();
  }, [props.form.id, props.getFormVerifiedEmails]);

  // Function to update email recipients count (for FormsRecipientDetail to call)
  const updateEmailRecipientsCount = async () => {
    try {
      const data = await props.getFormVerifiedEmails();
      setRecipientCount(data.data.length);
    } catch (e) {
      console.error('Error updating email recipients count:', e);
      setRecipientCount(0);
    }
  };

  // Compute AutoResponse data (memoized, only recomputes when parsedSpec changes)
  const autoResponseData = useMemo(() => {
    // Compute available variables based on form spec
    const availableVariables =
      props.hasFormSpec && props.parsedSpec?.fields
        ? Object.entries(props.parsedSpec.fields).map(([fieldName, field]) => ({
            name: fieldName,
            description: field.label || `Field: ${fieldName}`,
          }))
        : [
            { name: 'name', description: "Submitter's name" },
            { name: 'email', description: "Submitter's email address" },
            { name: 'message', description: 'Message content' },
            { name: 'company', description: 'Company/organization name' },
            { name: 'phone', description: 'Phone number' },
            { name: 'subject', description: 'Subject or topic' },
          ];

    // Generate a single complete email template example
    const nameVar = availableVariables[0]?.name || 'name';
    const templateExample = props.hasFormSpec
      ? [
          `Hello {{ ${nameVar} | "there" }},`,
          '',
          "Thank you for reaching out! We've received your submission and will review it shortly.",
          '',
          ...(availableVariables.length > 2
            ? [
                `Here's a summary of what you sent us:`,
                ...availableVariables.slice(1, 4).map((v) => `- ${v.description}: {{ ${v.name} }}`),
                '',
              ]
            : []),
          "We'll get back to you as soon as possible.",
          '',
          'Best regards,',
          'The Team',
        ].join('\n')
      : [
          'Hello {{ name | "there" }},',
          '',
          "Thank you for reaching out! We've received your message and will get back to you shortly.",
          '',
          "If you have any urgent questions, don't hesitate to call us directly.",
          '',
          'Best regards,',
          'The Team',
        ].join('\n');

    // Check if email field exists
    const hasEmailField =
      props.hasFormSpec && props.parsedSpec?.fields
        ? Object.values(props.parsedSpec.fields).some(
            (field) => field.field === 'email' || field.name.toLowerCase().includes('email')
          )
        : false;

    return {
      availableVariables,
      templateExample,
      hasEmailField,
    };
  }, [props.hasFormSpec, props.parsedSpec]);

  // Track auto_response values for validation (separate from ref to trigger validation useMemo)
  const [autoResponseSubjectForValidation, setAutoResponseSubjectForValidation] = useState(
    props.form.auto_response_subject || ''
  );
  const [autoResponseTextForValidation, setAutoResponseTextForValidation] = useState(
    props.form.auto_response_text || ''
  );

  // Memoized template validations - only validate when flag is true
  const EMPTY_VALIDATION = useMemo(() => ({ isValid: true, errors: [], warnings: [] }), []);

  const subjectValidation = useMemo(() => {
    if (!validateSubject || !autoResponseSubjectForValidation) {
      return EMPTY_VALIDATION;
    }
    return validateTemplateString(autoResponseSubjectForValidation);
  }, [autoResponseSubjectForValidation, validateSubject, EMPTY_VALIDATION]);

  const textValidation = useMemo(() => {
    if (!validateText || !autoResponseTextForValidation) {
      return EMPTY_VALIDATION;
    }
    return validateTemplateString(autoResponseTextForValidation);
  }, [autoResponseTextForValidation, validateText, EMPTY_VALIDATION]);

  // Increment to trigger reset in children
  const [resetCounter, setResetCounter] = useState(0);

  const cancelChanges = useCallback(() => {
    // Reset the ref to original values (copy from originalValuesRef)
    formDataRef.current = { ...originalValuesRef.current };
    setNotificationFormat(originalValuesRef.current.email_notification_format);
    setAutoResponseFormat(originalValuesRef.current.auto_response_format);
    setError(null);
    setFieldErrors({});
    // Trigger dirty re-check (will be false since we reset to original)
    setDirtyCheckCounter((c) => c + 1);
    // Trigger children to reset by incrementing counter
    setResetCounter((c) => c + 1);
  }, []);

  const submitForm = useCallback(async () => {
    const data = formDataRef.current;

    if (data.specs && data.specs.trim() !== '') {
      const specIsValid = parseAndValidateFormSpec(data.specs);
      if (!specIsValid.isValid) {
        let message = '';
        specIsValid.errors.forEach((value, key) => {
          message += `${key}: ${value}\n`;
        });
        setError({
          title: 'Form Specs Validation Error',
          message,
        });
        return;
      }
    }
    setError(null);

    const updatedForm: UpdateForm = {
      title: data.title,
      filter_spam: data.filter_spam,
      check_challenge: data.check_challenge,
      check_specs: data.check_specs,
      specs: data.specs,
      redirect_url: clearField(data.redirect_url),
      auto_response_enabled: data.auto_response_enabled,
      auto_response_subject: clearField(data.auto_response_subject),
      auto_response_text: clearField(data.auto_response_text),
      email_notification_format: data.email_notification_format,
      auto_response_format: data.auto_response_format,
      retention_days: data.retention_days,
      allowed_origins: data.allowed_origins,
    };
    setIsBusy(true);
    try {
      await props.submitFormCb(props.form.id, updatedForm);
      // Update original values to current values (now saved)
      originalValuesRef.current = { ...formDataRef.current };
      // Trigger dirty re-check (will be false since original now matches current)
      setDirtyCheckCounter((c) => c + 1);
    } catch (e) {
      setError({
        ...parseApiError(e),
      });
      if (hasFieldValidationError(e)) {
        const errors = axiosFieldValidationErrorToFormErrors(e);
        console.log(errors);
        setFieldErrors(errors);
      }
    } finally {
      setIsBusy(false);
    }
  }, [props.form.id, props.submitFormCb, t]);

  // Stable callbacks for child components to update the ref
  const handleFormFieldsChange = useCallback((values: Partial<FormFieldsValues>) => {
    Object.assign(formDataRef.current, values);
    // Trigger dirty state re-computation
    setDirtyCheckCounter((c) => c + 1);
  }, []);

  const handleFormatChange = useCallback(
    (field: 'email_notification_format' | 'auto_response_format', value: string) => {
      formDataRef.current[field] = value;
      setDirtyCheckCounter((c) => c + 1);
    },
    []
  );

  const handleAutoResponseChange = useCallback((field: string, value: any) => {
    (formDataRef.current as any)[field] = value;
    // Trigger dirty state re-computation
    setDirtyCheckCounter((c) => c + 1);
    // Update validation state for subject/text
    if (field === 'auto_response_subject') {
      setAutoResponseSubjectForValidation(value || '');
    } else if (field === 'auto_response_text') {
      setAutoResponseTextForValidation(value || '');
    } else if (field === 'auto_response_format') {
      setAutoResponseFormat(value);
    }
  }, []);

  // Field count drives the Form Fields summary + attention indicator.
  const fieldCount = useMemo(() => {
    if (!props.parsedSpec?.fields) return 0;
    return Object.keys(props.parsedSpec.fields).length;
  }, [props.parsedSpec]);

  // Mirror the prereq logic from AutoResponse.tsx so the lock state matches the alerts shown inside.
  const autoresponderLocked = useMemo(() => {
    const data = formDataRef.current;
    if (!data.check_challenge) return true;
    if (!hasEmailRecipients) return true;
    if (props.hasFormSpec && !autoResponseData.hasEmailField) return true;
    return false;
  }, [dirtyCheckCounter, hasEmailRecipients, props.hasFormSpec, autoResponseData.hasEmailField]);

  // General section summary: comma-joined feature toggles, fall back to default copy.
  const generalSummary = useMemo(() => {
    const data = formDataRef.current;
    const parts: string[] = [];
    if (data.filter_spam) parts.push(t('formEdit.summarySpamFilterOn'));
    if (data.check_challenge) parts.push(t('formEdit.summaryCaptchaOn'));
    if (data.retention_days != null) {
      parts.push(t('formEdit.summaryRetentionDays', { count: data.retention_days }));
    }
    if (parts.length === 0) return t('formEdit.generalDescription');
    return parts.join(' · ');
  }, [dirtyCheckCounter, t]);

  const fieldsSummary =
    fieldCount === 0
      ? t('formEdit.summaryNoFields')
      : t('formEdit.summaryFieldsCount', { count: fieldCount });
  const fieldsTone: 'gray' | 'yellow' = fieldCount === 0 ? 'yellow' : 'gray';

  // First few field labels for the Form Fields summary card preview line.
  const fieldNamesPreview = useMemo(() => {
    if (!props.parsedSpec?.fields) return '';
    const labels = Object.values(props.parsedSpec.fields).map((f) => f.label || f.name);
    if (labels.length === 0) return '';
    if (labels.length <= 3) return labels.join(', ');
    return `${labels.slice(0, 3).join(', ')} ${t('formEdit.fieldsPreviewMore', { count: labels.length - 3 })}`;
  }, [props.parsedSpec, t]);

  const recipientsSummary =
    recipientCount === 0
      ? t('formEdit.summaryNoRecipients')
      : t('formEdit.summaryRecipientsCount', { count: recipientCount });
  // Recipients are required for autoresponder — only highlight when AR is enabled.
  const recipientsTone: 'gray' | 'yellow' =
    formDataRef.current.auto_response_enabled && recipientCount === 0 ? 'yellow' : 'gray';

  const autoresponderSummary = autoresponderLocked
    ? t('formEdit.summaryLocked')
    : formDataRef.current.auto_response_enabled
      ? t('formEdit.summaryAutoresponderEnabled')
      : t('formEdit.summaryAutoresponderDisabled');

  const integrationsSummary =
    integrationCount === 0
      ? t('formEdit.summaryNoIntegrations')
      : t('formEdit.summaryIntegrationsCount', { count: integrationCount });

  const expandSection = useCallback((value: string) => {
    setOpenAccordionItem(value);
    setLockModalOpen(false);
    // Long page; scroll the settings region into view so the expanded item is visible.
    requestAnimationFrame(() => {
      const target = document.getElementById('settings');
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const autoresponderPrereqs: LockedSectionPrerequisite[] = useMemo(() => {
    const data = formDataRef.current;
    const list: LockedSectionPrerequisite[] = [
      {
        id: 'captcha',
        label: t('formEdit.autoresponderPrereq.captcha'),
        isMet: data.check_challenge,
        actionLabel: t('formEdit.openSection.general'),
        onAction: () => expandSection('form-fields'),
      },
      {
        id: 'recipient',
        label: t('formEdit.autoresponderPrereq.recipient'),
        isMet: hasEmailRecipients,
        actionLabel: t('formEdit.openSection.emailNotifications'),
        onAction: () => expandSection('forwarding'),
      },
    ];
    if (props.hasFormSpec) {
      list.push({
        id: 'emailField',
        label: t('formEdit.autoresponderPrereq.emailField'),
        isMet: autoResponseData.hasEmailField,
        actionLabel: t('formEdit.openSection.formFields'),
        onAction: () => expandSection('specifications'),
      });
    }
    return list;
  }, [
    dirtyCheckCounter,
    hasEmailRecipients,
    props.hasFormSpec,
    autoResponseData.hasEmailField,
    expandSection,
    t,
  ]);

  // Reject controlled-mode change attempts that would expand the locked autoresponder row.
  const handleAccordionChange = useCallback(
    (value: string | null) => {
      if (value === 'autoresponse' && autoresponderLocked) {
        setLockModalOpen(true);
        return;
      }
      setOpenAccordionItem(value);
    },
    [autoresponderLocked]
  );

  return (
    <>
      <Title order={2} mb="sm">
        {t('formEdit.settings')}
      </Title>

      {/* Warning for encrypted forms with file uploads */}
      {props.form.encryption_mode !== 'none' && props.hasFileField && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title={t('formEdit.fileUploadEncryptionWarningTitle')}
          color="yellow"
          mb="md"
        >
          {t('formEdit.fileUploadEncryptionWarning')}
        </Alert>
      )}

      <Accordion variant="contained" value={openAccordionItem} onChange={handleAccordionChange}>
        <Accordion.Item
          key={`accordion-${1}`}
          value="form-fields"
          style={attentionItemStyle('gray')}
        >
          <Accordion.Control>
            <AccordionRowHeader
              icon={<IconSettings size={20} />}
              title={t('formEdit.general')}
              summary={generalSummary}
              tone="gray"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <FormFields
              isEditing={true}
              isBusy={isBusy}
              hasError={error}
              fieldErrors={fieldErrors}
              submitCb={submitForm}
              formSubmitUrl={props.formSubmitUrl}
              initialValues={{
                title: props.form.title,
                filter_spam: props.form.filter_spam,
                check_challenge: props.form.check_challenge,
                redirect_url: props.form.redirect_url || undefined,
                auto_response_enabled: props.form.auto_response_enabled,
                retention_days: props.form.retention_days ?? null,
                allowed_origins: props.form.allowed_origins ?? [],
              }}
              onValuesChange={handleFormFieldsChange}
              hasUnsavedChanges={isDirty}
              onCancel={cancelChanges}
              resetKey={resetCounter}
              maxRetentionDays={maxRetentionDays}
              allowNullRetention={props.form.retention_days == null}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item
          key={`accordion-${2}`}
          value="specifications"
          style={attentionItemStyle(fieldsTone)}
        >
          <Accordion.Control>
            <AccordionRowHeader
              icon={<IconChecklist size={20} />}
              title={t('formEdit.formFieldsOptional')}
              summary={fieldsSummary}
              tone={fieldsTone}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={2} style={{ minWidth: 0 }}>
                  <Text fw={500}>
                    {fieldCount > 0
                      ? t('formEdit.summaryFieldsCount', { count: fieldCount })
                      : t('formEdit.summaryNoFields')}
                  </Text>
                  {fieldNamesPreview && (
                    <Text size="sm" c="dimmed">
                      {fieldNamesPreview}
                    </Text>
                  )}
                </Stack>
                <Button
                  component={Link}
                  to={`/account/forms/${props.form.id}/fields`}
                  rightSection={<IconArrowRight size={16} />}
                >
                  {t('formEdit.openFieldEditor')}
                </Button>
              </Group>
              {props.form.check_specs === false && (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  {t('formEdit.fieldsDisabledNotice')}
                </Alert>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item
          key={`accordion-${3}`}
          value="forwarding"
          style={attentionItemStyle(recipientsTone)}
        >
          <Accordion.Control>
            <AccordionRowHeader
              icon={<IconMailForward size={20} />}
              title={t('formEdit.emailNotifications')}
              summary={recipientsSummary}
              tone={recipientsTone}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="md">
              <FormsRecipientDetail
                form={props.form}
                submitFormCb={props.submitFormRecipientCb}
                getVerifiedEmails={useRustyState.getState().getAndSetVerifiedEmails}
                getFormVerifiedEmails={props.getFormVerifiedEmails}
                deleteRecipientCp={props.deleteRecipientCp}
                onRecipientsChange={updateEmailRecipientsCount}
              />
              <Select
                label="Email format"
                description="Text-only emails look more personal. HTML emails include branding and styling."
                size="sm"
                value={notificationFormat}
                onChange={(value) => {
                  if (value) {
                    setNotificationFormat(value);
                    handleFormatChange('email_notification_format', value);
                  }
                }}
                data={[
                  { label: 'HTML + Text', value: 'html' },
                  { label: 'Text only', value: 'text' },
                ]}
              />
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item
          key={`accordion-${4}`}
          value="autoresponse"
          style={autoresponderLocked ? { opacity: 0.7 } : undefined}
        >
          <Accordion.Control style={autoresponderLocked ? { cursor: 'not-allowed' } : undefined}>
            <AccordionRowHeader
              icon={<IconMail size={20} />}
              title={t('formEdit.autoresponse')}
              summary={autoresponderSummary}
              tone="gray"
              locked={autoresponderLocked}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <AutoResponse
              isBusy={isBusy}
              hasError={error}
              submitCb={submitForm}
              hasUnsavedChanges={isDirty}
              onCancel={cancelChanges}
              hasEmailRecipients={hasEmailRecipients}
              hasFormSpec={props.hasFormSpec}
              availableVariables={autoResponseData.availableVariables}
              templateExample={autoResponseData.templateExample}
              hasEmailField={autoResponseData.hasEmailField}
              subjectValidation={subjectValidation}
              textValidation={textValidation}
              onValidateSubject={setValidateSubject}
              onValidateText={setValidateText}
              formValues={{
                check_challenge: props.form.check_challenge,
                check_specs: props.form.check_specs,
                auto_response_enabled: props.form.auto_response_enabled,
                auto_response_subject: props.form.auto_response_subject,
                auto_response_text: props.form.auto_response_text,
                auto_response_format: autoResponseFormat,
              }}
              onFormChange={handleAutoResponseChange}
              resetKey={resetCounter}
              onSendTest={async (email: string) => {
                await useRustyState.getState().api.sendAutoResponseTest(props.form.id, email);
              }}
              userEmail={useRustyState.getState().verifiedEmails.find((e) => e.is_verified)?.email}
            />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item
          key={`accordion-${5}`}
          value="integrations"
          style={attentionItemStyle('gray')}
        >
          <Accordion.Control>
            <AccordionRowHeader
              icon={<IconWebhook size={20} />}
              title={t('formEdit.integrations')}
              summary={integrationsSummary}
              tone="gray"
            />
          </Accordion.Control>
          <Accordion.Panel>
            <FormsIntegrationsPanel form={props.form} onIntegrationsChange={setIntegrationCount} />
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
      <LockedSectionModal
        opened={lockModalOpen}
        onClose={() => setLockModalOpen(false)}
        title={t('formEdit.lockModalTitle')}
        description={t('formEdit.lockModalBodyAutoresponder')}
        prerequisites={autoresponderPrereqs}
      />
      <FormEditActionBar
        isDirty={isDirty}
        dirtyCount={dirtyCount}
        isSaving={isBusy}
        assistantOpen={props.chatOpened}
        onSave={submitForm}
        onToggleAssistant={props.onToggleChat}
        onDelete={props.onDelete}
      />
    </>
  );
}
